import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import {
    BuildRounded,
    CheckCircleRounded,
    DoNotDisturbOnRounded,
    ErrorOutlineRounded,
    ExpandLessRounded,
    ExpandMoreRounded,
} from '@mui/icons-material';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import isActionErrorAi from '@/ai/types/action-error-ai';
import { useTranslation } from 'react-i18next';
import AndieToolNetworkChip from './AndieToolNetworkChip';

const AndieGenericToolRender = function <RESULT = object, ARGS = Record<string, unknown>>(
    props: ActionRenderAi<RESULT, ARGS> & {
        title: (args?: ARGS) => React.ReactNode;
        loadingMessages: (args?: ARGS) => string[];
        completeMessage: (props: { result?: RESULT; args?: ARGS }) => string;
        additionalComponent?: React.FC<ActionRenderAi<RESULT, ARGS>>;
        isError?: (result?: RESULT) => boolean;
        icon?: React.ReactNode;
        loadingMessageInterval?: number;
        /** Auto-expand the details panel once the tool completes. Defaults to true. */
        autoExpandOnComplete?: boolean;
        /** Raw network code/name from args, shown as a disambiguator chip. */
        network?: string;
        /** NetProfileAuth id resolved to a network name when `network` is absent. */
        privateProfileId?: string;
    },
) {
    const { t } = useTranslation();
    const {
        title,
        icon = <BuildRounded sx={{ fontSize: 16 }} />,
        loadingMessages,
        loadingMessageInterval = 2000,
        completeMessage,
        additionalComponent: AdditionalComponent,
        autoExpandOnComplete = true,
        isError,
        network,
        privateProfileId,
        ...actionProps
    } = props;

    const { status, args, result } = actionProps;

    const isComplete = status === ActionResponseStatusAi.COMPLETE;
    const isCancelled = status === ActionResponseStatusAi.CANCELLED;
    const hasError = typeof isError === 'function' ? isError(result) : isActionErrorAi(result);
    const hasDetails = !!AdditionalComponent && !hasError && !isCancelled;

    const [currentIdx, setCurrentIdx] = useState(0);
    const loadingMsgs = loadingMessages(args);

    useEffect(() => {
        if (!isComplete && !isCancelled && loadingMsgs.length > 1) {
            const interval = setInterval(() => {
                setCurrentIdx((prev) => (prev + 1) % loadingMsgs.length);
            }, loadingMessageInterval);
            return () => clearInterval(interval);
        }
    }, [isComplete, loadingMsgs.length, loadingMessageInterval, isCancelled]);

    // Collapsed while running (keeps streaming light), auto-expanded once finished.
    const [expanded, setExpanded] = useState(false);
    const didAutoExpand = useRef(false);
    useEffect(() => {
        if (isComplete && hasDetails && autoExpandOnComplete && !didAutoExpand.current) {
            didAutoExpand.current = true;
            setExpanded(true);
        }
    }, [isComplete, hasDetails, autoExpandOnComplete]);

    // Errors and cancellations are intentionally muted so an unfinished step
    // reads as a quiet skip rather than an alarming red flag.
    const statusColor =
        hasError || isCancelled ? 'text.disabled' : isComplete ? 'success.main' : 'andie.main';

    const message = hasError
        ? t('andie.tool.error')
        : isCancelled
          ? t('andie.tool.cancelled', { defaultValue: 'Cancelled' })
          : isComplete
            ? completeMessage({ result, args })
            : loadingMsgs[currentIdx];

    const StatusIcon = (
        <Box
            sx={{
                display: 'flex',
                position: 'relative',
                width: 16,
                height: 16,
                color: statusColor,
                flexShrink: 0,
                ...(!isComplete &&
                    !hasError &&
                    !isCancelled && {
                        animation: 'andie-tool-pulse 1.5s ease-in-out infinite',
                        '@keyframes andie-tool-pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                        },
                    }),
            }}
        >
            {hasError ? (
                <ErrorOutlineRounded sx={{ fontSize: 16 }} />
            ) : isCancelled ? (
                <DoNotDisturbOnRounded sx={{ fontSize: 16 }} />
            ) : isComplete ? (
                <CheckCircleRounded sx={{ fontSize: 16 }} />
            ) : (
                icon
            )}
        </Box>
    );

    const header = (
        <Stack
            direction="row"
            alignItems="center"
            gap={0.75}
            sx={{ minWidth: 0, width: '100%', flexWrap: 'wrap', rowGap: 0 }}
        >
            <Typography variant="caption" color="text.primary" fontWeight={600} noWrap>
                {title(args)}
            </Typography>
            {(network || privateProfileId) && (
                <>
                    <Typography variant="caption" color="text.disabled">
                        ·
                    </Typography>
                    <AndieToolNetworkChip network={network} privateProfileId={privateProfileId} />
                </>
            )}
            <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={`${isComplete}-${hasError}-${currentIdx}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ minWidth: 0, display: 'flex', alignItems: 'center' }}
                >
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {message}
                    </Typography>
                </motion.div>
            </AnimatePresence>
        </Stack>
    );

    return (
        <Stack gap={0.5}>
            {hasDetails ? (
                <ButtonBase
                    onClick={() => setExpanded((v) => !v)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 0.75,
                        alignSelf: 'flex-start',
                        maxWidth: '100%',
                        borderRadius: 1,
                        px: 0.5,
                        py: 0.25,
                    }}
                >
                    {StatusIcon}
                    {header}
                    {expanded ? (
                        <ExpandLessRounded sx={{ color: 'text.disabled', fontSize: 16, flexShrink: 0 }} />
                    ) : (
                        <ExpandMoreRounded sx={{ color: 'text.disabled', fontSize: 16, flexShrink: 0 }} />
                    )}
                </ButtonBase>
            ) : (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.75}
                    sx={{ alignSelf: 'flex-start', maxWidth: '100%', px: 0.5, py: 0.25 }}
                >
                    {StatusIcon}
                    {header}
                </Stack>
            )}
            <AnimatePresence initial={false}>
                {expanded && AdditionalComponent && !hasError && (
                    <motion.div
                        key="tool-details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <Box sx={{ borderLeft: '2px solid', borderColor: 'andie.main', pl: 1, ml: 0.5 }}>
                            <AdditionalComponent {...actionProps} />
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Stack>
    );
};
export default AndieGenericToolRender;
